/**
 * PHARMAKON — supplier avoirs: the SPA twin of the server CONFIG `apps/server/src/config/avoir.config.js`
 * (refonte Achats · Stock WP-29; D-10, D-11, D-12; design/ux/03_avoirs_bc_reclamations.md §1.4, §1.7,
 * §1.15, §2.4, §10). The server derives its lists from the Prisma enums; the browser cannot import
 * Prisma, so this file repeats them once — `apps/server/src/__tests__/avoirs.parity.test.js` fails
 * the build on the first drift (PK-BUG-15: a value copied instead of imported drifts silently).
 */

/** Prisma `TypeAvoir` — ECART « crédit fournisseur » (D-10, D-38), PRODUITS, FINANCIER « ristourne » (D-10). */
export const TYPE_AVOIR = Object.freeze({ PRODUITS: "PRODUITS", FINANCIER: "FINANCIER", ECART: "ECART" });

/** Prisma `NatureLigneAvoir` (D-11). */
export const NATURE_LIGNE_AVOIR = Object.freeze({ MANQUANT: "MANQUANT", RETOUR: "RETOUR" });

/** Prisma `MotifRetourFournisseur` (D-11: Cassé · Périmé · Erreur de livraison). */
export const MOTIF_RETOUR_FOURNISSEUR = Object.freeze({
  CASSE: "CASSE",
  PERIME: "PERIME",
  ERREUR_LIVRAISON: "ERREUR_LIVRAISON",
});

/** D-32 (supersedes D-12) / OP-03-01 — the types whose CONFIRMED credit reduces the fournisseur solde (never the ristourne). */
export const TYPES_AVOIR_SOLDE = Object.freeze([TYPE_AVOIR.ECART, TYPE_AVOIR.PRODUITS]);

/** Prisma `StatutComptableAvoir` (refonte M-20, WP-77, D-32) — INSTANCE: pending, outside the solde; CONFIRME: in it. */
export const STATUT_COMPTABLE_AVOIR = Object.freeze({ INSTANCE: "INSTANCE", CONFIRME: "CONFIRME" });
export const LIBELLES_STATUT_COMPTABLE_AVOIR = Object.freeze({
  [STATUT_COMPTABLE_AVOIR.INSTANCE]: "En instance",
  [STATUT_COMPTABLE_AVOIR.CONFIRME]: "Confirmé",
});

/**
 * Prisma `DecisionStockLigneAvoir` (refonte M-24, WP-84, D-33) — the stock decision each avoir produits
 * line persists, and its words (the editor's dialog and the drawer say them; the server DTO also serves
 * `decision_stock_libelle`).
 */
export const DECISION_STOCK_LIGNE_AVOIR = Object.freeze({
  DEDUIRE: "DEDUIRE",
  NE_PAS_DEDUIRE: "NE_PAS_DEDUIRE",
  DEJA_DEDUIT_RECLAMATION: "DEJA_DEDUIT_RECLAMATION",
  ENTREE_IMMEDIATE: "ENTREE_IMMEDIATE",
  ENTREE_DIFFEREE: "ENTREE_DIFFEREE",
  AUCUN_MOUVEMENT: "AUCUN_MOUVEMENT",
});
export const LIBELLES_DECISION_STOCK_LIGNE_AVOIR = Object.freeze({
  [DECISION_STOCK_LIGNE_AVOIR.DEDUIRE]: "Déduit du stock",
  [DECISION_STOCK_LIGNE_AVOIR.NE_PAS_DEDUIRE]: "Stock inchangé",
  [DECISION_STOCK_LIGNE_AVOIR.DEJA_DEDUIT_RECLAMATION]: "Déjà déduit par la réclamation",
  [DECISION_STOCK_LIGNE_AVOIR.ENTREE_IMMEDIATE]: "Entré en stock à la création",
  [DECISION_STOCK_LIGNE_AVOIR.ENTREE_DIFFEREE]: "Entrée en stock à l'arrivée de la facture",
  [DECISION_STOCK_LIGNE_AVOIR.AUCUN_MOUVEMENT]: "Hors stock — en attente d'enlèvement",
});
/** The decisions an invoice-line avoir line may send (server `DECISIONS_STOCK_LIGNE_FACTURE`). */
export const DECISIONS_STOCK_LIGNE_FACTURE = Object.freeze([
  DECISION_STOCK_LIGNE_AVOIR.DEDUIRE,
  DECISION_STOCK_LIGNE_AVOIR.NE_PAS_DEDUIRE,
  DECISION_STOCK_LIGNE_AVOIR.DEJA_DEDUIT_RECLAMATION,
]);

/**
 * Refonte WP-85 (M-26, D-34; CP §5.4) — a « Reçu non facturé » line (no invoice line): the answer
 * Commandé / Non commandé (Prisma `RecuNonFactureCommande`), the stock decisions each admits, the
 * collection state of a non commandé line (Prisma `EtatEnlevementLigneAvoir`) and the states of the
 * reader `GET /avoirs/recus-non-factures` (server `ETAT_RECU_NON_FACTURE`).
 */
export const RECU_NON_FACTURE_COMMANDE = Object.freeze({ COMMANDE: "COMMANDE", NON_COMMANDE: "NON_COMMANDE" });
export const LIBELLES_RECU_NON_FACTURE_COMMANDE = Object.freeze({
  [RECU_NON_FACTURE_COMMANDE.COMMANDE]: "Commandé",
  [RECU_NON_FACTURE_COMMANDE.NON_COMMANDE]: "Non commandé",
});
export const DECISIONS_STOCK_RECU_NON_FACTURE = Object.freeze({
  [RECU_NON_FACTURE_COMMANDE.COMMANDE]: Object.freeze([DECISION_STOCK_LIGNE_AVOIR.ENTREE_IMMEDIATE, DECISION_STOCK_LIGNE_AVOIR.ENTREE_DIFFEREE]),
  [RECU_NON_FACTURE_COMMANDE.NON_COMMANDE]: Object.freeze([DECISION_STOCK_LIGNE_AVOIR.AUCUN_MOUVEMENT]),
});
export const ETAT_ENLEVEMENT_LIGNE_AVOIR = Object.freeze({ EN_ATTENTE: "EN_ATTENTE", ENLEVE: "ENLEVE" });
export const LIBELLES_ETAT_ENLEVEMENT_LIGNE_AVOIR = Object.freeze({
  [ETAT_ENLEVEMENT_LIGNE_AVOIR.EN_ATTENTE]: "En attente d'enlèvement",
  [ETAT_ENLEVEMENT_LIGNE_AVOIR.ENLEVE]: "Enlevé par le fournisseur",
});
export const ETAT_RECU_NON_FACTURE = Object.freeze({ A_FACTURER: "A_FACTURER", A_ENLEVER: "A_ENLEVER" });
export const LIBELLES_ETAT_RECU_NON_FACTURE = Object.freeze({
  [ETAT_RECU_NON_FACTURE.A_FACTURER]: "En attente de facture",
  [ETAT_RECU_NON_FACTURE.A_ENLEVER]: "En attente d'enlèvement",
});
/** The entry bounds of a « Reçu non facturé » line — the purchase line's (server `RECU_NON_FACTURE_SAISIE`). */
export const RECU_NON_FACTURE_SAISIE = Object.freeze({ numero_lot_max: 100, quantite_max: 50_000, prix_ht_max: 500_000, ppa_max: 100_000 });

/**
 * Refonte WP-90 (M-27, D-35; CP §6, §6.1) — « Régler l'avoir produits » per quantity: the resolution modes
 * (Prisma `ModeResolutionLigneAvoir`), the ones `POST /avoirs/:id/resolutions` records (server
 * `MODES_RESOLUTION_SAISISSABLES` — the delivery and, refonte WP-91, the financial credit), the mode a « Reçu non
 * facturé » line is resolved by through its own flow, a line's state, why an avoir / a line is not
 * settleable, and the delivery's entry bounds (server `RESOLUTION_SAISIE`).
 */
export const MODE_RESOLUTION_LIGNE_AVOIR = Object.freeze({
  LIVRAISON: "LIVRAISON",
  AVOIR_FINANCIER: "AVOIR_FINANCIER",
  FACTURE_ULTERIEURE: "FACTURE_ULTERIEURE",
  ENLEVEMENT: "ENLEVEMENT",
});
export const LIBELLES_MODE_RESOLUTION_LIGNE_AVOIR = Object.freeze({
  [MODE_RESOLUTION_LIGNE_AVOIR.LIVRAISON]: "Produits livrés par le fournisseur",
  [MODE_RESOLUTION_LIGNE_AVOIR.AVOIR_FINANCIER]: "Avoir financier",
  [MODE_RESOLUTION_LIGNE_AVOIR.FACTURE_ULTERIEURE]: "Facture du fournisseur",
  [MODE_RESOLUTION_LIGNE_AVOIR.ENLEVEMENT]: "Enlèvement par le fournisseur",
});
export const MODES_RESOLUTION_SAISISSABLES = Object.freeze([MODE_RESOLUTION_LIGNE_AVOIR.LIVRAISON, MODE_RESOLUTION_LIGNE_AVOIR.AVOIR_FINANCIER]);
export const MODE_RESOLUTION_RECU_NON_FACTURE = Object.freeze({
  [RECU_NON_FACTURE_COMMANDE.COMMANDE]: MODE_RESOLUTION_LIGNE_AVOIR.FACTURE_ULTERIEURE,
  [RECU_NON_FACTURE_COMMANDE.NON_COMMANDE]: MODE_RESOLUTION_LIGNE_AVOIR.ENLEVEMENT,
});
export const ETAT_RESOLUTION_LIGNE = Object.freeze({ EN_ATTENTE: "EN_ATTENTE", PARTIELLE: "PARTIELLE", REGLEE: "REGLEE" });
export const LIBELLES_ETAT_RESOLUTION_LIGNE = Object.freeze({
  EN_ATTENTE: "En attente",
  PARTIELLE: "Réglée partiellement",
  REGLEE: "Réglée",
});
export const RAISON_NON_REGLABLE_PAR_QUANTITE = Object.freeze({
  ANNULE: "ANNULE",
  TYPE: "TYPE",
  CONFIRME: "CONFIRME",
  REGLE: "REGLE",
  SANS_LIGNE_REGLABLE: "SANS_LIGNE_REGLABLE",
});
export const RAISON_LIGNE_NON_REGLABLE = Object.freeze({ RECU_NON_FACTURE: "RECU_NON_FACTURE", REGLEE: "REGLEE", SANS_FACTURE: "SANS_FACTURE" });
export const RESOLUTION_SAISIE = Object.freeze({ numero_lot_max: 100, quantite_max: 50_000, ppa_max: 100_000, reference_max: 60, observations_max: 500 });

/** Prisma `ModeRepartitionAvoir` (refonte M-20, CP §8.2) — written from WP-81. */
export const MODE_REPARTITION_AVOIR = Object.freeze({ GLOBAL: "GLOBAL", PAR_FACTURE: "PAR_FACTURE" });
export const LIBELLES_MODE_REPARTITION_AVOIR = Object.freeze({
  [MODE_REPARTITION_AVOIR.GLOBAL]: "Montant global",
  [MODE_REPARTITION_AVOIR.PAR_FACTURE]: "Répartition par facture",
});

/**
 * ux/03 §1.4 — the three label forms, identical in every file. Refonte WP-81 (CP §8.3, D-38): the Écart
 * avoir is a « crédit fournisseur » with 0..n factures, the ristourne a refund booked as revenue.
 */
export const LIBELLES_TYPE_AVOIR = Object.freeze({
  [TYPE_AVOIR.ECART]: Object.freeze({ long: "Avoir financier — crédit fournisseur", court: "Crédit fournisseur", compact: "Crédit fourn." }),
  [TYPE_AVOIR.PRODUITS]: Object.freeze({ long: "Avoir produits", court: "Produits", compact: "Produits" }),
  [TYPE_AVOIR.FINANCIER]: Object.freeze({ long: "Avoir financier — ristourne / remboursement (recette)", court: "Ristourne", compact: "Ristourne" }),
});

/** D-11 — line natures and return motifs, French (ux/03 §1.7). */
export const LIBELLES_NATURE_LIGNE_AVOIR = Object.freeze({
  [NATURE_LIGNE_AVOIR.MANQUANT]: "Manquant à la livraison",
  [NATURE_LIGNE_AVOIR.RETOUR]: "Retour",
});
export const LIBELLES_MOTIF_RETOUR = Object.freeze({
  [MOTIF_RETOUR_FOURNISSEUR.CASSE]: "Cassé",
  [MOTIF_RETOUR_FOURNISSEUR.PERIME]: "Périmé",
  [MOTIF_RETOUR_FOURNISSEUR.ERREUR_LIVRAISON]: "Erreur de livraison",
});

/** D2.4 — the facture motifs of the candidates list. */
export const MOTIF_FACTURE_AVOIR = Object.freeze({ QT: "QT", MONEY: "MONEY" });

/** ux/03 §2.4 — the derived état of an avoir. */
export const ETAT_AVOIR = Object.freeze({
  EN_ATTENTE: "EN_ATTENTE",
  REGLE_PARTIELLEMENT: "REGLE_PARTIELLEMENT",
  REGLE: "REGLE",
  ANNULE: "ANNULE",
});
export const LIBELLES_ETAT_AVOIR = Object.freeze({
  EN_ATTENTE: "En attente",
  REGLE_PARTIELLEMENT: "Réglé partiellement",
  REGLE: "Réglé",
  ANNULE: "Annulé",
});

/** ux/03 §10 — platform-governed bounds the editor checks before the server does. */
export const AVOIR_BORNES = Object.freeze({
  motif_min: 5,
  motif_max: 500,
  reference_max: 60,
  suggestions_page: 20,
  factures_max: 50,
  lignes_max: 500,
  // refonte WP-81 / WP-82 (CP §8.2, A-107 (2)) — from this many factures the crédit fournisseur's
  // allocation method is required (no default): the editor asks « Montant global » or « Répartition
  // par facture » (server `AVOIR_FACTURES_MODE_REQUIS`)
  factures_mode_requis: 2,
});

/** The confirmations the create asks for (422 `AVOIR_CONFIRMATION_REQUISE`, `details.confirmation`). */
export const CONFIRMATION_AVOIR = Object.freeze({
  QUANTITES_MODIFIEES: "QUANTITES_MODIFIEES",
  DEPASSEMENT_ECART: "DEPASSEMENT_ECART",
});

/** The avoir error codes the editor reacts to (server `ERROR_CODES`, refonte WP-28). */
export const ERREUR_AVOIR = Object.freeze({
  INVALIDE: "AVOIR_INVALIDE",
  QUANTITE_DEPASSEE: "AVOIR_QUANTITE_DEPASSEE",
  CONFIRMATION_REQUISE: "AVOIR_CONFIRMATION_REQUISE",
  // Refonte WP-78 — the lifecycle transitions (409): already confirmed, an origin already confirmed /
  // credited, a reused idempotency key with another request
  DEJA_CONFIRME: "AVOIR_DEJA_CONFIRME",
  ORIGINE_CONFIRMEE: "AVOIR_ORIGINE_CONFIRMEE",
  REQUETE_CONFLIT: "IDEMPOTENCY_CONFLICT",
  // Trésorerie TWP-57 — « Imputer le crédit » of an avoir: not confirmed yet (409), nothing left outside
  // the factures (409), a part above its facture's cap or a total above the credit (422)
  EN_INSTANCE: "AVOIR_EN_INSTANCE",
  CREDIT_DEJA_IMPUTE: "AVOIR_CREDIT_DEJA_IMPUTE",
  IMPUTATION_DEPASSEE: "AVOIR_IMPUTATION_DEPASSEE",
  // Refonte WP-84 — a « Toujours demander » motif sent without « Déduire » / « Ne pas déduire » (422)
  DECISION_STOCK_REQUISE: "AVOIR_DECISION_STOCK_REQUISE",
  // Refonte WP-90 — quantities already resolved: no whole confirmation, no cancellation (409)
  REGLE_PAR_QUANTITE: "AVOIR_REGLE_PAR_QUANTITE",
  // Refonte WP-91 — the credit named to settle quantities already settles others / is linked (409)
  CREDIT_DEJA_LIE: "AVOIR_CREDIT_DEJA_LIE",
});

/** Refonte WP-78 — the idempotency key of a creation / confirmation (server `REQUETE_ID_AVOIR`). */
export const REQUETE_ID_AVOIR = Object.freeze({ longueur_min: 8, longueur_max: 64 });

/**
 * Refonte WP-30 — the « Journal des avoirs fournisseur » (ux/03 §2): the server's sort whitelist,
 * page sizes and request bounds (`AVOIR_JOURNAL_TRIS`, `PAGINATION_AVOIR`, … of the server CONFIG).
 */
export const AVOIR_JOURNAL = Object.freeze({
  tris: Object.freeze(["date_avoir", "numero_avoir", "montant_ttc", "created_at", "fournisseur", "type_avoir"]),
  limit: 20,
  limit_max: 100,
  recherche_max: 80,
  filtre_valeurs_max: 50,
});

/** OP-03-10 — who may cancel an avoir already (partly) settled (en attente: `avoir:annuler`, P + M). */
export const ROLES_ANNULATION_AVOIR_REGLE = Object.freeze(["PROPRIETAIRE"]);

/** ux/03 §2.7 (A-02, OP-03-11) — the matching state of a connected fournisseur's credit note. */
export const RAPPROCHEMENT_NOTE = Object.freeze({ A_RAPPROCHER: "A_RAPPROCHER", RAPPROCHE: "RAPPROCHE" });
export const SUGGESTION_RAPPROCHEMENT = Object.freeze({ MEME_FACTURE: "MEME_FACTURE", MEME_MONTANT: "MEME_MONTANT" });

/** Prisma `TypeNoteDeCredit` (the fournisseur plane's credit notes) and its pharmacy-side words. */
export const TYPE_NOTE_DE_CREDIT = Object.freeze({
  AVOIR_CREDIT: "AVOIR_CREDIT",
  REMBOURSEMENT: "REMBOURSEMENT",
  REMPLACEMENT: "REMPLACEMENT",
});
export const LIBELLES_TYPE_NOTE_DE_CREDIT = Object.freeze({
  AVOIR_CREDIT: "Avoir crédit",
  REMBOURSEMENT: "Remboursement",
  REMPLACEMENT: "Remplacement",
});
